// Story 2.3 — query hook for GET /api/v1/admin/portfolios/:id/drop.
//
// Returns `{ data: AdminDropDetail | null }`. The detail page Section 2
// branches on `data == null` (render release form) vs `data != null`
// (render live-drop summary).

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../../lib/api-client";

export type AdminDropDetail = {
  drop_id: string;
  portfolio_id: string;
  kind: string;
  status: "scheduled" | "live" | "retiring" | "retired" | string;
  price_cents: number;
  per_user_cap: number | null;
  scheduled_at: string;
  live_at: string | null;
  retiring_at: string | null;
  retired_at: string | null;
  linked_bulletin_post_id: string | null;
  linked_bulletin_slug: string | null;
  linked_bulletin_title: string | null;
  is_perpetual: boolean;
  created_at: string;
};

export type AdminDropForPortfolioResponse = {
  data: AdminDropDetail | null;
};

async function fetchAdminDropForPortfolio(
  portfolioId: string,
): Promise<AdminDropForPortfolioResponse> {
  const response = await fetch(
    `/api/v1/admin/portfolios/${portfolioId}/drop`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const raw = await response.json();
      if (raw?.error?.code) message = raw.error.code;
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminDropForPortfolioResponse;
}

export function useAdminDropForPortfolio(portfolioId: string | null | undefined) {
  return useQuery({
    queryKey: ["admin-drop-for-portfolio", portfolioId],
    queryFn: () => fetchAdminDropForPortfolio(portfolioId as string),
    enabled: Boolean(portfolioId),
    staleTime: 30_000,
  });
}
